import type { MessageKey } from 'shared/i18n';
import { useMemo, useCallback } from 'react';
import { useSlateSelector, useSlateStatic, ReactEditor } from 'slate-react';
import { Editor as SlateEditor, Element } from 'slate';
import { TagWithMetadata, useContinueTag, useCreateTag, useConcept, useDocumentTaxonomyConfig } from 'entities/tag';
import { useCreateAndInsertTag, selectSelectionElementId, applyBlockTagBinding } from 'slate/extensions/tag';
import { useTagSelection } from 'app/state/contexts/TagContext';
import { getTagType } from 'app/utils/tag-utils';
import { isEscapedConceptType } from 'app/utils/tag-utils/getConceptNarrowType';
import { findChainInsertionPoint } from './tagChainUtils';
import { useApplyCellTags } from './useApplyCellTags';
import { BlockScope, ScopeAxis } from './ScopeToggle';
import { BlockCarrier, BlockSelection as CarrierBlockSelection, carrierHoldsAnyTag } from './blockCarrier';
import { useEditorLocale } from 'app/api/documents/useEditorData';
import { useEditorDocument } from 'app/api/documents/useEditorData';
import { useEditorCoarse, useEditorLiveSection } from 'app/state/contexts/EditorUiContext';

interface UseTagInfoContinuationParams {
  selectedTag: TagWithMetadata | null;
  isContentMarked: boolean;
  documentTags: TagWithMetadata[];
  onCancel?: () => void;
}

type TableCell = { row: number; col: number; value?: string | number | null; conceptId?: string; linkroleUri?: string | null };
type TableSelection = {
  element?: Element;
  referenceDataSuperId: string;
  cells: TableCell[];
  onTagsCreated?: () => void;
};
type DraftTagRef = { onId?: string };

export const useTagInfoContinuation = ({ selectedTag, isContentMarked, documentTags, onCancel }: UseTagInfoContinuationParams) => {
  const { mutate: createTag } = useCreateTag();
  const { mutate: continueTag } = useContinueTag();
  const createAndInsertTag = useCreateAndInsertTag();
  const activeSection = useEditorCoarse().activeSection as { uuid: string };
  const section = useEditorLiveSection() as { superId: string };
  const { data: pDocument } = useEditorDocument();
  const locale = useEditorLocale();
  const conceptName = selectedTag?.concept?.name ?? null;
  const { data: conceptData } = useConcept(conceptName, pDocument?.superId);
  const {
    draftTag: draftTagRaw,
    tableSelection: tableSelectionRaw,
    blockSelection: blockSelectionRaw,
    blockScope,
    setBlockScope,
    clearBlockSelection,
    clearTableTagSelection,
  } = useTagSelection();
  const draftTag = draftTagRaw as DraftTagRef | null;
  const tableSelection = tableSelectionRaw as TableSelection | null;
  const blockSelection = blockSelectionRaw as CarrierBlockSelection | null;
  const { data: config } = useDocumentTaxonomyConfig(pDocument?.superId);
  const taxonomyId = config?.taxonomySelections?.find((s) => s.isActive)?.taxonomy?.id ?? null;
  const editor = useSlateStatic();
  const applyCellTags = useApplyCellTags();

  const hasTableSelection = !!tableSelection?.cells?.length;
  const isProseSelection = (blockSelection?.kind === 'paragraph' || blockSelection?.kind === 'list') && !hasTableSelection;
  const hasImageTarget = blockSelection?.kind === 'image' && !hasTableSelection;

  const isSourceBlockTag = !!selectedTag?.metadata?.blockCarrier;
  const isNumericSource = selectedTag?.type === 'nonFraction';

  const scopeAxis: ScopeAxis = isProseSelection ? 'prose' : 'table';
  const conceptType = conceptData?.conceptType ?? selectedTag?.concept?.conceptType;
  const scopeLocked: BlockScope | null = isSourceBlockTag
    ? isProseSelection
      ? 'block'
      : 'table'
    : isNumericSource
    ? isProseSelection
      ? 'text'
      : 'cell'
    : isProseSelection
    ? isEscapedConceptType(conceptType)
      ? 'block'
      : 'text'
    : null;
  const scope: BlockScope = (scopeLocked ?? blockScope ?? (isProseSelection ? 'text' : 'cell')) as BlockScope;
  const showScopeToggle = (hasTableSelection || isProseSelection) && scopeLocked === null;

  const proseWholeTarget = isProseSelection && scope === 'block';
  const hasTextTarget = isContentMarked && !hasTableSelection && (!blockSelection || (isProseSelection && !proseWholeTarget));

  const conceptId = conceptData?.id ?? selectedTag?.conceptId ?? '';
  const sectionContext = {
    documentSuperId: pDocument?.superId,
    sectionSuperId: section?.superId,
    pageId: activeSection?.uuid,
  };

  const selectionElementId = useSlateSelector(selectSelectionElementId) ?? draftTag?.onId ?? null;

  const tableBlockCarrier = useMemo((): BlockCarrier | null => {
    const el = tableSelection?.element;
    if (!el?.uuid) return null;
    try {
      return { path: ReactEditor.findPath(editor, el), elementId: el.uuid };
    } catch {
      return null;
    }
  }, [tableSelection, editor]);

  const existingTagsWithSameConcept = useMemo(() => {
    if (!selectedTag || !conceptName || !documentTags) return [];
    return documentTags.filter((t) => t.concept?.name === conceptName && !t.metadata?.unplaced);
  }, [selectedTag, conceptName, documentTags]);

  const tableWholeTarget = hasTableSelection && scope === 'table' && !!tableBlockCarrier;
  const tableCellTarget = hasTableSelection && scope === 'cell';

  const blockTargetPath = tableWholeTarget ? tableBlockCarrier?.path : hasImageTarget || proseWholeTarget ? blockSelection?.path : undefined;
  const carrierHoldsConcept = useMemo(
    () =>
      !!blockTargetPath &&
      carrierHoldsAnyTag(
        editor,
        blockTargetPath,
        existingTagsWithSameConcept.map((t) => t.id)
      ),
    [blockTargetPath, existingTagsWithSameConcept, editor]
  );

  const hasTarget = (hasTextTarget || hasImageTarget || hasTableSelection || proseWholeTarget) && !carrierHoldsConcept;

  const targetChainable = hasTextTarget || hasImageTarget || tableWholeTarget || proseWholeTarget;
  const canContinue = !isNumericSource && targetChainable && !carrierHoldsConcept;
  const continueDisabledHint: MessageKey | null = carrierHoldsConcept
    ? 'tag:footer.block.concept.on.carrier'
    : !isNumericSource && tableCellTarget
    ? 'tag:footer.cell.no.chain'
    : null;

  const cellTagType = useMemo(() => {
    if (!selectedTag) return 'nonFraction';
    const baseXsdType = selectedTag.concept?.conceptType?.baseXsdType ?? selectedTag.concept?.conceptType?.baseXbrliType;
    return selectedTag.type ?? (baseXsdType ? getTagType(baseXsdType) : 'nonFraction');
  }, [selectedTag]);

  // --- Continuation handlers (write a nextSegment link) ----------------------

  const continueIntoBlock = useCallback(
    (carrier: BlockCarrier) => {
      const sections = pDocument?.content?.sections ?? [];
      const { predecessor, successor } = findChainInsertionPoint(existingTagsWithSameConcept, editor, section.superId, sections, carrier.path);
      if (!predecessor && !successor) return;

      continueTag(
        {
          taxonomyId: taxonomyId ?? '',
          ...sectionContext,
          onId: carrier.elementId,
          conceptId,
          lang: locale,
          type: selectedTag?.type ?? 'nonNumeric',
          linkroleUri: selectedTag?.linkroleUri ?? null, // inherit the source tag's role (a chain is one fact)
          predecessorId: predecessor?.id,
          successorId: successor?.id,
        },
        {
          onSuccess: (created) => {
            if (created?.id) applyBlockTagBinding(editor, carrier.path, { tagId: created.id, continuedAt: successor?.id ?? undefined });
            clearBlockSelection?.();
            clearTableTagSelection?.();
          },
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      existingTagsWithSameConcept,
      editor,
      pDocument,
      section,
      activeSection,
      conceptId,
      taxonomyId,
      locale,
      selectedTag,
      continueTag,
      clearBlockSelection,
      clearTableTagSelection,
    ]
  );

  const handleTextContinuation = useCallback(() => {
    if (!selectedTag || !isContentMarked || existingTagsWithSameConcept.length === 0) return;
    const sections = pDocument?.content?.sections ?? [];
    const { predecessor, successor } = findChainInsertionPoint(existingTagsWithSameConcept, editor, section.superId, sections);
    if (!predecessor && !successor) return;

    const selectedText = editor.selection ? SlateEditor.string(editor, editor.selection) : undefined;
    createAndInsertTag({
      editor,
      tagType: conceptName ?? '',
      tagData: {
        taxonomyId: taxonomyId ?? '',
        ...sectionContext,
        onId: selectionElementId,
        conceptId,
        lang: locale,
        type: selectedTag.type ?? 'nonNumeric',
        value: selectedText || undefined,
        linkroleUri: selectedTag.linkroleUri ?? null, // inherit the source tag's role
      },
      continuationTag: predecessor ? { tagId: predecessor.id, tagType: predecessor.concept?.name ?? conceptName ?? '' } : null,
      successorTag: successor ? { tagId: successor.id } : null,
      callbacks: {},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTag,
    isContentMarked,
    existingTagsWithSameConcept,
    editor,
    pDocument,
    section,
    activeSection,
    selectionElementId,
    conceptName,
    conceptId,
    taxonomyId,
    locale,
    createAndInsertTag,
  ]);

  const handleContinuation = useCallback(() => {
    if (!canContinue) return;
    if (tableWholeTarget && tableBlockCarrier) {
      continueIntoBlock(tableBlockCarrier);
    } else if ((hasImageTarget || proseWholeTarget) && blockSelection) {
      continueIntoBlock({ path: blockSelection.path, elementId: blockSelection.elementId });
    } else {
      handleTextContinuation();
    }
  }, [canContinue, tableWholeTarget, tableBlockCarrier, hasImageTarget, proseWholeTarget, blockSelection, continueIntoBlock, handleTextContinuation]);

  const createStandaloneBlock = useCallback(
    (carrier: BlockCarrier) => {
      createTag(
        {
          taxonomyId: taxonomyId ?? '',
          ...sectionContext,
          onId: carrier.elementId,
          conceptId,
          lang: locale,
          type: 'nonNumeric',
          linkroleUri: selectedTag?.linkroleUri ?? null, // inherit the source tag's role
        },
        {
          onSuccess: (created) => {
            if (created?.id) applyBlockTagBinding(editor, carrier.path, { tagId: created.id });
            clearBlockSelection?.();
            clearTableTagSelection?.();
          },
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, conceptId, taxonomyId, locale, createTag, clearBlockSelection, clearTableTagSelection, activeSection, section, pDocument]
  );

  const standaloneIntoCells = useCallback(async () => {
    if (!tableSelection?.cells?.length) return;
    await applyCellTags({
      mode: 'create',
      cells: tableSelection.cells,
      referenceDataSuperId: tableSelection.referenceDataSuperId,
      conceptId,
      linkroleUri: selectedTag?.linkroleUri ?? null, // inherit the source tag's role
      type: cellTagType,
      taxonomyId: taxonomyId ?? '',
      sectionContext,
      locale,
      onTagsCreated: tableSelection.onTagsCreated,
      blockedTagIds: new Set(existingTagsWithSameConcept.map((t) => t.id)),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableSelection, conceptId, cellTagType, taxonomyId, locale, applyCellTags, existingTagsWithSameConcept, activeSection, section, pDocument]);

  const handleStandalone = useCallback(() => {
    if (!hasTarget) return;
    if (tableCellTarget) {
      void standaloneIntoCells();
    } else if (tableWholeTarget && tableBlockCarrier) {
      createStandaloneBlock(tableBlockCarrier);
    } else if ((hasImageTarget || proseWholeTarget) && blockSelection) {
      createStandaloneBlock({ path: blockSelection.path, elementId: blockSelection.elementId });
    } else if (hasTextTarget) {
      const selectedText = editor.selection ? SlateEditor.string(editor, editor.selection) : undefined;
      createAndInsertTag({
        editor,
        tagType: conceptName ?? '',
        tagData: {
          taxonomyId: taxonomyId ?? '',
          ...sectionContext,
          onId: selectionElementId,
          conceptId,
          lang: locale,
          type: selectedTag?.type ?? 'nonNumeric',
          value: selectedText || undefined,
        },
        continuationTag: null,
        callbacks: {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasTarget,
    tableCellTarget,
    tableWholeTarget,
    tableBlockCarrier,
    hasImageTarget,
    proseWholeTarget,
    blockSelection,
    hasTextTarget,
    standaloneIntoCells,
    createStandaloneBlock,
    editor,
    conceptName,
    conceptId,
    taxonomyId,
    locale,
    selectionElementId,
    selectedTag,
    createAndInsertTag,
  ]);

  const handleCancel = useCallback(() => {
    clearTableTagSelection?.();
    clearBlockSelection?.();
    onCancel?.();
  }, [clearTableTagSelection, clearBlockSelection, onCancel]);

  return {
    handleContinuation,
    handleStandalone,
    handleCancel,
    hasTarget,
    canContinue,
    continueDisabledHint,
    showScopeToggle,
    scopeAxis,
    scope,
    setScope: setBlockScope,
  };
};
